import {useCallback, useMemo, type ReactNode} from "react";
import {Loader} from "@mantine/core";
import {t} from "i18next";
import {
    DIBDocumentGenerator,
    DIBDocumentGeneratorForm,
    DIBDocumentGeneratorView,
} from "@dibkunnskap/dokumentgenerator";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import RenderNodeFactoryLegacy from "../RenderNodeFactoryLegacy";
import {ruleset} from "../renderRules";
import {augmentations} from "../augmentations";
import documentClasses from "./document.module.css";
import {DocumentGeneratorInput} from "./DocumentGeneratorInput";
import {ConditionalsForm} from "./ConditionalsForm";
import {FormLabel} from "./FormLabel";
import {DropdownMenu} from "./DropdownMenu";
import {DropdownMenuItem} from "./DropdownMenuItem";
import {OptionsForm} from "./OptionsForm";
import {XAlternativesForm} from "./XAlternativesForm";
import {XListForm} from "./XListForm";
import {OptionalForm} from "./OptionalForm";
import {UndoButton} from "./UndoButton";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentGeneratorBodyProps {
    /** topicId of the document template to render. */
    documentId: string;
    /** collectionId (filling). Undefined = standalone OR checklist-draft. */
    collectionId?: string;
    /** Whether user has no saved collection (only true in checklist-draft case). */
    isDraft: boolean;
    /** Callback to open "create filling" modal. Standalone passes nothing. */
    onPromptNewFilling?: () => void;

    // ── Slots ─────────────────────────────────────────────
    /** Above the form/preview split. Checklist puts <h1>name</h1> here; standalone leaves empty. */
    outerHeader?: ReactNode;
    /** Inside the form pane, above the form. Standalone puts <DIBDocumentGeneratorReset>; checklist leaves empty. */
    topOfForm?: ReactNode;
    /** Inside the preview pane, above the rendered document. Standalone puts <h1>name</h1> + descroot; checklist leaves empty. */
    topOfPreview?: ReactNode;
    /** Inside the form pane, below the form. Standalone puts <GenerateButtons isStandalone>; checklist leaves empty. */
    bottomOfForm?: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DocumentGeneratorBody = ({
    documentId,
    collectionId,
    isDraft,
    onPromptNewFilling,
    outerHeader,
    topOfForm,
    topOfPreview,
    bottomOfForm,
}: DocumentGeneratorBodyProps) => {
    // ── Data fetching ────────────────────────────────────────────────

    const {data: docData, isFetching} =
        dibAppApi.endpoints.getDocgenDocument.useQuery(
            {resourceId: documentId, collectionId},
            {refetchOnMountOrArgChange: true}
        );

    const {data: conditionsData, isFetching: isConditionsFetching} =
        dibAppApi.endpoints.getResourceConditions.useQuery(
            {resourceId: docData?.topicData?.id ?? ""},
            {skip: !docData?.topicData?.id}
        );

    const {data: conditionUserData} =
        dibAppApi.endpoints.getResourceConditionUserData.useQuery({
            topicId: documentId,
            collectionId,
        });

    // ── Mutations ────────────────────────────────────────────────────

    const [triggerPatchXVar] = dibAppApi.endpoints.patchXVariable.useMutation();
    const [triggerSetCondition] =
        dibAppApi.endpoints.setResourceCondition.useMutation();
    const [triggerAutofill] =
        dibAppApi.endpoints.autofillDocumentForOrgNr.useLazyQuery();
    const [triggerResetXVars] = dibAppApi.endpoints.resetXVariables.useMutation();
    const [triggerResetConditions] =
        dibAppApi.endpoints.resetConditionalUserData.useMutation();

    // ── Derived values ───────────────────────────────────────────────

    const documentIds = useMemo(
        () => ({topicId: documentId, collectionId}),
        [documentId, collectionId]
    );

    const showLoader = isFetching || isConditionsFetching;

    // dokumentgenerator expects a react-intl intl object; shim it with i18next
    const intlShim = useMemo(
        () => ({formatMessage: ({id}: {id: string}) => t(id)}),
        []
    );

    // ── Callbacks ────────────────────────────────────────────────────

    const handleSetConditionLabel = useCallback(
        (payload: {conditionLabelGroup: string; conditionLabel: string}) => {
            triggerSetCondition({
                documentIds,
                ...payload,
            });
        },
        [triggerSetCondition, documentIds]
    );

    const handlePromptNewFilling = useCallback(
        (e?: React.FocusEvent) => {
            if (!isDraft) return;
            (e?.target as HTMLElement)?.blur?.();
            onPromptNewFilling?.();
        },
        [isDraft, onPromptNewFilling]
    );

    const handleSelectCompany = useCallback(
        ({
            orgId,
            variableId,
            triggerId,
        }: {
            orgId: string;
            variableId: string;
            triggerId: string;
        }) => {
            triggerAutofill({
                orgnr: orgId,
                variableId,
                triggerName: triggerId,
                documentIds,
            });
        },
        [triggerAutofill, documentIds]
    );

    const handleResetDocument = useCallback(() => {
        triggerResetXVars({
            topicId: documentId,
            collectionId,
        });
        triggerResetConditions({topicId: documentId, collectionId});
    }, [triggerResetXVars, triggerResetConditions, documentId, collectionId]);

    // ── Render ───────────────────────────────────────────────────────

    return (
        <div className={documentClasses.documentView}>
            {outerHeader && (
                <header className={documentClasses.documentViewHeader}>
                    {outerHeader}
                </header>
            )}

            {showLoader && (
                <div
                    style={{
                        gridColumn: "1 / -1",
                        textAlign: "center",
                        padding: "2rem",
                    }}
                >
                    <Loader size="md" />
                </div>
            )}

            {docData?.topicData && !isFetching && !isConditionsFetching && (
                <DIBDocumentGenerator
                    key={docData.topicData.id}
                    documentData2020={docData.topicData}
                    resourceConditionsData={conditionsData ?? {}}
                    conditionsUserData={conditionUserData ?? []}
                    onSetVariableValue={(args: any) =>
                        triggerPatchXVar({...args, ...documentIds})
                    }
                    onSetConditionLabel={handleSetConditionLabel}
                    onSelectProffCompany={handleSelectCompany}
                    onResetDocument={handleResetDocument}
                    intl={intlShim}
                    renderNodeFactory={RenderNodeFactoryLegacy}
                    ruleset={ruleset}
                    augmentations={augmentations}
                    isLoading={isFetching}
                    orgnrSearchUri="/dibapi/api/orgsearch"
                    autosuggestUri="/dibappapi/api/document/autosuggestions"
                    options={{
                        focusOnClick: false,
                        preventProffLookup: false,
                        preventScroll: false,
                        preventSearch: false,
                        readOnly: false,
                        isDraft,
                    }}
                >
                    <div className={documentClasses.documentForm}>
                        {topOfForm && (
                            <div className={documentClasses.documentFormTop}>
                                {topOfForm}
                            </div>
                        )}
                        <div className={documentClasses.documentFormScroll}>
                            <DIBDocumentGeneratorForm
                            onRenderInput={(
                                inputProps: any,
                                comment: string,
                                companySearchEnabled: boolean,
                                setOpen: (val: boolean) => void,
                                suggestionList: string[],
                                searching: boolean
                            ) => (
                                <DocumentGeneratorInput
                                    inputProps={inputProps}
                                    comment={comment}
                                    companySearchEnabled={companySearchEnabled}
                                    setDropdownOpen={setOpen}
                                    suggestionList={suggestionList}
                                    searching={searching}
                                    isDraft={isDraft}
                                    onPromptNewFilling={handlePromptNewFilling}
                                />
                            )}
                            onRenderConditionLabels={(props: any) => (
                                <ConditionalsForm {...props} />
                            )}
                            onRenderLabel={(name: string, props: any) => (
                                <FormLabel value={name} {...props} />
                            )}
                            onRenderMenu={(props: any) => (
                                <DropdownMenu {...props} />
                            )}
                            onRenderMenuItem={(props: any) => (
                                <DropdownMenuItem {...props} />
                            )}
                            onRenderOptions={(props: any) => (
                                <OptionsForm
                                    {...props}
                                    onFocus={handlePromptNewFilling}
                                />
                            )}
                            onRenderXAlternativesForm={(props: any) => (
                                <XAlternativesForm
                                    {...props}
                                    onFocus={handlePromptNewFilling}
                                />
                            )}
                            onRenderXListForm={(props: any) => (
                                <XListForm
                                    {...props}
                                    onFocus={handlePromptNewFilling}
                                />
                            )}
                            onRenderXOptionalForm={(props: any) => {
                                const varId =
                                    docData?.topicData?.objects[props.id]?.data
                                        ?.varId;
                                const heading = varId
                                    ? docData?.topicData?.objects[varId]?.data
                                          ?.heading
                                    : undefined;
                                return (
                                    <OptionalForm
                                        {...props}
                                        heading={heading}
                                        onFocus={handlePromptNewFilling}
                                    />
                                );
                            }}
                            onRenderXVarComment={() => null}
                            onRenderXVarReset={(props: any) => (
                                <UndoButton
                                    onClick={props.onClick}
                                    ref={props.ref}
                                />
                            )}
                            />
                        </div>
                        {bottomOfForm && (
                            <div className={documentClasses.documentFormBottom}>
                                {bottomOfForm}
                            </div>
                        )}
                    </div>
                    <div className={documentClasses.documentPreview}>
                        {topOfPreview}
                        <DIBDocumentGeneratorView />
                    </div>
                </DIBDocumentGenerator>
            )}
        </div>
    );
};
